import {
  IFormUpdaters,
  TControlName,
  TUpdaterName,
} from '@infotech-soe/metaschema';

import { TActionArgsValues } from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CategoryViewModel from '../../category/view-model/CategoryViewModel';
import IContextService from '../../context/IContextService';
import { DatasetCurrentState } from '../../dataset/view-model/DatasetCurrentState';
import { DictionaryOutputs } from '../../dictionary/IDictionaryService';
import UniqueId from '../../uid/UniqueId';
import { FormUpdateStrategy } from '../IFormService';
import {
  FormControlViewModel,
  FormControlViewModelType,
  ImageInputFormControlViewModel,
  NumericInputFormControlViewModel,
} from '../view-model/FormControlViewModel';
import { FormSource, FormSourceType } from '../view-model/FormSource';
import {
  FormUpdateViewModel,
  FormUpdateWithDictOutputViewModel,
  FormViewModel,
} from '../view-model/FormViewModel';

import clearForm from './clearForm';
import { inputUpdaters } from './inputs';
import updateDict from './inputs/dictInput';
import updateWithSchemaUpdater, { executeUpdater } from './usingSchemaUpdater';

const hasUpdater = <T extends keyof typeof inputUpdaters>(
  type: string
): type is T => Object.keys(inputUpdaters).includes(type);

const isNumericInput = (
  control: FormControlViewModel
): control is NumericInputFormControlViewModel =>
  control.type === FormControlViewModelType.NUMERIC_INPUT;

const isNumericValue = (n: any): n is number =>
  !Number.isNaN(n) && (n || n === 0);

class FormUpdaterService {
  constructor(private readonly contextService: IContextService) {}

  public async runSingleUpdater(
    formState: FormViewModel,
    controlName: TControlName,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    updaterName?: TUpdaterName
  ): Promise<FormUpdateViewModel> {
    const updaters = formState.updater;
    const control = formState.controls[controlName];

    if (formState.source.type === FormSourceType.PREDEFINED) {
      throw new Error(`${formState.source.type} forms don't have updaters`);
    }

    if (!updaters) {
      throw new Error("This form doesn't have updaters");
    }

    const updaterNameFromArg =
      !!updaterName && !!updaters[updaterName] && updaterName;
    const updaterNameFromControl =
      'updater' in control && !!control.updater && control.updater;
    const updater = updaterNameFromArg || updaterNameFromControl;

    if (updater) {
      return this.runUpdater(
        formState,
        category,
        datasetsState,
        updater,
        controlName
      );
    }

    const apiVars = new Map(formState.apiVars);
    return {
      controls: {},
      args: {},
      apiVars,
      triggered: false,
    };
  }

  private async runClearUpdater(
    formState: FormViewModel,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>
  ): Promise<FormUpdateViewModel> {
    let clearUpdater = '';
    if (formState?.clear && 'updater' in formState.clear) {
      clearUpdater = formState.clear.updater;
      return this.runUpdater(formState, category, datasetsState, clearUpdater);
    }
    throw new Error("This form doesn't have clear updater");
  }

  private async runUpdater(
    formState: FormViewModel,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    updaterName: string,
    controlName = ''
  ): Promise<FormUpdateViewModel> {
    const updaters = formState.updater;
    const apiVars = new Map(formState.apiVars);

    if (formState.source.type === FormSourceType.PREDEFINED) {
      throw new Error(`${formState.source.type} forms don't have updaters`);
    }

    if (!updaters) {
      throw new Error("This form doesn't have updater");
    }

    const updaterResult = await executeUpdater(
      this.contextService,
      category,
      datasetsState,
      formState.source,
      apiVars,
      updaters[updaterName],
      formState.args,
      controlName,
      formState.utils || {},
      formState.controls,
      {}
    );

    if (updaterResult) {
      return updaterResult;
    }

    return {
      controls: {},
      args: {},
      apiVars,
      triggered: false,
    };
  }

  public async updateWithOutParams(
    form: FormViewModel,
    field: TControlName,
    outputs: DictionaryOutputs,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>
  ): Promise<FormUpdateWithDictOutputViewModel> {
    const apiVars = new Map(form.apiVars);
    let result: FormUpdateWithDictOutputViewModel = {
      controls: {},
      args: {},
      skipNextUpdater: false,
      apiVars,
      triggered: false,
    };
    const control = form.controls[field];
    if (
      (control.type === FormControlViewModelType.DICTIONARY_INPUT ||
        control.type === FormControlViewModelType.TREE_INPUT ||
        control.type === FormControlViewModelType.DICT_DATE ||
        control.type === FormControlViewModelType.DICT_MULTILINE) &&
      control.outParams !== undefined
    ) {
      result = await updateDict(
        field,
        control.outParams,
        outputs,
        form.controls,
        control.category,
        apiVars
      );
      const resultControl = result.controls[field];
      if (
        resultControl &&
        (resultControl.type === FormControlViewModelType.DICTIONARY_INPUT ||
          resultControl.type === FormControlViewModelType.TREE_INPUT ||
          resultControl.type === FormControlViewModelType.DICT_DATE ||
          resultControl.type === FormControlViewModelType.DICT_MULTILINE)
      ) {
        const updaterResult = await this.runFormSchemaUpdater(
          form,
          field,
          resultControl.value,
          category,
          datasetsState,
          result.args
        );
        result.controls = {
          ...result.controls,
          ...updaterResult.controls,
        };

        result.args = {
          ...result.args,
          ...updaterResult.args,
        };
        // todo: uncomment after resolve lost focus issue
        // result.skipNextUpdater = updaterResult.triggered;
        result.skipNextUpdater = false;
        result.apiVars = updaterResult.apiVars;
      }
    }
    return result;
  }

  public async runFormSchemaUpdater(
    formState: FormViewModel,
    controlName: TControlName,
    value: any,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    args?: TActionArgsValues
  ): Promise<FormUpdateViewModel> {
    const apiVars = new Map(formState.apiVars);
    let result: FormUpdateViewModel = {
      controls: {},
      args: {},
      apiVars,
      triggered: false,
    };
    if (formState.updater && !formState.skipNextUpdater) {
      const updaterResult = await updateWithSchemaUpdater(
        this.contextService,
        formState.updater as IFormUpdaters,
        formState.utils || {},
        formState.source as FormSource,
        value,
        controlName,
        formState.controls[controlName],
        formState.controls,
        { ...formState.args, ...args },
        apiVars,
        category,
        datasetsState
      );

      if (updaterResult) {
        result = updaterResult;
      }
    }
    return result;
  }

  public async clearForm(
    formState: FormViewModel,
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>
  ): Promise<FormUpdateViewModel> {
    if ('updater' in (formState?.clear ?? {})) {
      return this.runClearUpdater(formState, category, datasetsState);
    }

    return clearForm(formState);
  }

  public async updateForm(
    formState: FormViewModel,
    values: TActionArgsValues,
    strategy: FormUpdateStrategy
  ): Promise<FormUpdateViewModel> {
    if (strategy === FormUpdateStrategy.REPLACE) {
      return this.replaceArgs(formState, values);
    } else if (strategy === FormUpdateStrategy.UPDATE) {
      return this.updateSingleField(formState, values);
    }

    throw new Error(`wrong form update strategy: ${strategy}`);
  }

  private validateInput(control: FormControlViewModel, value: any): void {
    if (isNumericInput(control)) {
      control.invalid =
        isNumericValue(value) &&
        ((isNumericValue(control.min) && value < control.min) ||
          (isNumericValue(control.max) && value > control.max));
    }
  }

  private async replaceArgs(
    formState: FormViewModel,
    args: TActionArgsValues
  ): Promise<FormUpdateViewModel> {
    const apiProvider = new MerpsApiProvider();
    const result: FormUpdateViewModel = {
      controls: {},
      args,
      apiVars: new Map(formState.apiVars),
      triggered: false,
    };

    for (const [arg, value] of Object.entries(args)) {
      if (formState.controls[arg]) {
        const control = formState.controls[arg];

        if (control.hasValue) {
          let val = value;
          if (control.type === FormControlViewModelType.IMAGE && value) {
            if (value.id || value.streamId) {
              val = await apiProvider.streams.read(value.id || value.streamId);
              delete args[arg];
            }
          }
          result.controls[arg] = {
            ...control,
            value: val,
          };

          this.validateInput(result.controls[arg], value);
        }
      }
    }

    return result;
  }

  private async updateSingleField(
    formState: FormViewModel,
    values: TActionArgsValues
  ): Promise<FormUpdateViewModel> {
    const apiVars = new Map(formState.apiVars);
    const result: FormUpdateViewModel = {
      controls: {},
      args: {},
      apiVars,
      triggered: false,
    };

    const [controlName, value] = Object.entries(values)[0];

    const control = { ...formState.controls[controlName], value };

    this.validateInput(control, value);

    result.controls[controlName] = control;

    if (hasUpdater(control.type)) {
      result.args[controlName] = inputUpdaters[control.type](control, value);
    } else if (control.type === FormControlViewModelType.IMAGE) {
      result.args[controlName] = null;
      (result.controls[controlName] as ImageInputFormControlViewModel).changed =
        true;
    } else {
      result.args[controlName] = value;
    }

    return result;
  }
}

export default FormUpdaterService;
